import { getSubdomainFranchise } from '../utils/get-subdomain';
import { httpInterceptor } from './http.interceptor';

export interface RecompensaItem {
  id: number;
  nome: string;
  categoria_nome: string;
  valor: number;
  imagem: string;
  aditional_images?: any[];
  descricao?: string;
  qntd_disponivel: number;
  ativo?: boolean;
}

export interface AdesaoResponse {
  id: number;
  user_id: number;
  status: 'pendente' | 'aprovado' | 'reprovado';
  saldo: number;
  obs?: string;
  created_at: string;
  updated_at: string;
}

export interface ResgateRequest {
  userid: number;
  itemid: number;
}

export interface ExtratoDiamante {
  id: number;
  user_id: number;
  tipo: 'credito' | 'debito';
  valor: number;
  descricao: string;
  created_at: string;
}

export interface ItemCategoria {
  id: number;
  nome: string;
  count?: number;
}

export interface ExtratoMovimentacao {
  id: number;
  user_id: number;
  venda_id: number | null;
  quantity: number;
  action: 'credito_venda' | 'credito_bonus' | 'resgate_pendente' | 'resgate_aprovado' | 'resgate_reprovado' | 'debito_outros';
  item_id: number | null;
  date_expiration: string | null;
  date_created: string;
  observation: string | null;
  codepagarme: string | null;
  nome_cliente: string | null;
  item_nome: string | null;
  item_imagem: string | null;
}

export interface PaginationMeta {
  page: number;
  size: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

class DiamondsService {
  private subdomain: string;

  constructor() {
    this.subdomain = getSubdomainFranchise();
  }

  private getHeaders(useMatriz: boolean = false): HeadersInit {
    return {
      'Content-Type': 'application/json',
      'x-api-key': btoa(useMatriz ? 'matriz' : this.subdomain),
    };
  }

  // Adesões - equivalente aos métodos do Angular
  async getAdesoes(): Promise<AdesaoResponse[]> {
    try {
      const response = await httpInterceptor.get('/diamonds/aprovations', {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  async getAdesao(userId: number): Promise<AdesaoResponse> {
    try {
      const response = await httpInterceptor.get(`/diamonds/aprovation/${userId}`, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  async aderirPrograma(userId: number): Promise<any> {
    try {
      const response = await httpInterceptor.post('/diamonds/aprovation', {
        userid: userId
      }, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao aderir ao programa:', erro);
      throw erro;
    }
  }

  async updateAprovationObs(id: number, obs: string): Promise<any> {
    try {
      const response = await httpInterceptor.patch(`/diamonds/aprovation-obs/${id}`, {
        obs: obs
      }, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  async updateAprovationStatus(id: number, status: string): Promise<any> {
    try {
      const response = await httpInterceptor.patch(`/diamonds/aprovation-status/${id}`, {
        status: status
      }, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      throw erro;
    }
  }

  // Itens - equivalente aos métodos do Angular
  async getItensPaginados(
    page: number,
    size: number,
    search: string,
    ativo?: boolean,
    dateStart?: string,
    dateEnd?: string,
  ): Promise<{ statusCode: number; data: RecompensaItem[]; pagination: PaginationMeta }> {
    try {
      const params = new URLSearchParams({
        page: String(page),
        size: String(size),
      });
      if (search && search.trim() !== '') params.append('search', search.trim());
      if (ativo !== undefined) params.append('ativo', String(ativo));
      if (dateStart) params.append('dateStart', dateStart);
      if (dateEnd) params.append('dateEnd', dateEnd);
      const response = await httpInterceptor.get(`/diamonds/items-paginados?${params.toString()}`, {
        headers: this.getHeaders(true),
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao buscar itens paginados:', erro);
      throw erro;
    }
  }

  async getItens(): Promise<RecompensaItem[]> {
    try {
      const response = await httpInterceptor.get('/diamonds/items', {
        headers: this.getHeaders(true) // Usa 'matriz' como no Angular
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao buscar itens:', erro);
      throw erro;
    }
  }

  async getDetailItem(idItem: number): Promise<RecompensaItem> {
    try {
      const response = await httpInterceptor.get(`/diamonds/item/${idItem}`, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao buscar detalhes do item:', erro);
      throw erro;
    }
  }

  async createItem(item: any): Promise<any> {
    try {
      const response = await httpInterceptor.post('/diamonds/item', item, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao criar item:', erro);
      throw erro;
    }
  }

  async updateItem(id: number, itemData: any, images: any): Promise<any> {
    try {
      const response = await httpInterceptor.patch(`/diamonds/item/${id}`, {
        itemData: itemData,
        images: images
      }, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao atualizar item:', erro);
      throw erro;
    }
  }

  async deleteItem(id: number): Promise<any> {
    try {
      const response = await httpInterceptor.delete(`/diamonds/item/${id}`, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao deletar item:', erro);
      throw erro;
    }
  }

  async updateAtivoItem(id: number, ativo: boolean): Promise<any> {
    try {
      const response = await httpInterceptor.patch(`/diamonds/item/${id}/ativo`, {
        ativo: ativo
      }, {
        headers: this.getHeaders()
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || 'Erro ao atualizar status do item');
      }
      return data;
    } catch (erro) {
      console.error('Erro ao atualizar status ativo do item:', erro);
      throw erro;
    }
  }

  async updateAtivoItemBulk(ids: number[], ativo: boolean): Promise<any> {
    try {
      const response = await httpInterceptor.patch('/diamonds/items/ativo-bulk', {
        ids,
        ativo
      }, {
        headers: this.getHeaders()
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || 'Erro ao atualizar status dos itens em massa');
      }
      return data;
    } catch (erro) {
      console.error('Erro ao atualizar status ativo dos itens em massa:', erro);
      throw erro;
    }
  }

  // Resgates - equivalente aos métodos do Angular
  async resgatar(userid: number, itemid: number): Promise<any> {
    try {
      const response = await httpInterceptor.post('/diamonds/withdrawal', {
        userid,
        itemid
      }, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao resgatar item:', erro);
      throw erro;
    }
  }

  async updateResgateSolicitado(id: number, status: string): Promise<any> {
    try {
      const response = await httpInterceptor.patch(`/diamonds/withdrawal-status/${id}`, {
        status: status
      }, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao atualizar status do resgate:', erro);
      throw erro;
    }
  }

  async updateResgatesEmMassa(ids: number[], status: string): Promise<any> {
    try {
      const response = await httpInterceptor.patch('/diamonds/withdrawal-status/bulk', {
        ids,
        status,
      }, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao atualizar resgates em massa:', erro);
      throw erro;
    }
  }

  async updateResgateSolicitadoFranchise(id: number, status: string): Promise<any> {
    try {
      const response = await httpInterceptor.patch(`/diamonds/withdrawal-status/franchise/${id}`, {
        status: status
      }, {
        headers: this.getHeaders(true) // Usa 'matriz'
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao atualizar status do resgate da franquia:', erro);
      throw erro;
    }
  }

  // Extratos e Statements - equivalente aos métodos do Angular
  async getStatements(userId: number): Promise<ExtratoDiamante[]> {
    try {
      const response = await httpInterceptor.get(`/diamonds/statements/${userId}`, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao buscar extratos:', erro);
      throw erro;
    }
  }

  async getMovimentacao(userId: number): Promise<ExtratoMovimentacao[]> {
    try {
      const response = await httpInterceptor.get(`/diamonds/statements/${userId}`, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao buscar movimentações:', erro);
      throw erro;
    }
  }

  async getStatementsAdmin(): Promise<ExtratoDiamante[]> {
    try {
      const response = await httpInterceptor.get('/diamonds/statements', {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao buscar extratos admin:', erro);
      throw erro;
    }
  }

  async getStatementsWithdrawal(): Promise<any[]> {
    try {
      const response = await httpInterceptor.get('/diamonds/statements-withdrawal', {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao buscar extratos de resgates:', erro);
      throw erro;
    }
  }

  async getStatementsWithdrawalFranchise(): Promise<any[]> {
    try {
      const response = await httpInterceptor.get('/diamonds/statements-withdrawal/franchise', {
        headers: this.getHeaders(true) // Usa 'matriz'
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao buscar extratos de resgates da franquia:', erro);
      throw erro;
    }
  }

  // Categorias - novo método baseado no Angular
  async getItemsCategories(): Promise<ItemCategoria[]> {
    try {
      const response = await httpInterceptor.get('/diamonds/items-categories', {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao buscar categorias:', erro);
      throw erro;
    }
  }

  // Gráficos e Rankings - equivalente aos métodos do Angular
  async getGraphWithdrawXCosts(): Promise<any> {
    try {
      const response = await httpInterceptor.get('/diamonds/graph-data/withdraw-x-costs', {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao buscar gráfico de resgates x custos:', erro);
      throw erro;
    }
  }

  async getRankingItemsWithdraw(): Promise<any[]> {
    try {
      const response = await httpInterceptor.get('/diamonds/ranking-withdraw-items', {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao buscar ranking de itens resgatados:', erro);
      throw erro;
    }
  }

  // Diamantes manuais - equivalente ao método do Angular
  async manualDiamonds(data: any): Promise<any> {
    try {
      const response = await httpInterceptor.post('/diamonds/manual-diamonds', data, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao processar diamantes manuais:', erro);
      throw erro;
    }
  }

  // Dashboard - Estatísticas de diamantes
  private buildDateQuery(startDate?: string, endDate?: string): string {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const query = params.toString();
    return query ? `?${query}` : '';
  }

  async getDiamondsBySales(startDate?: string, endDate?: string): Promise<any> {
    try {
      const response = await httpInterceptor.get(`/diamonds/diamonds-by-sales${this.buildDateQuery(startDate, endDate)}`, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao buscar diamantes por vendas:', erro);
      throw erro;
    }
  }

  async getDiamondsByPurchases(startDate?: string, endDate?: string): Promise<any> {
    try {
      const response = await httpInterceptor.get(`/diamonds/diamonds-by-purchases${this.buildDateQuery(startDate, endDate)}`, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao buscar diamantes por compras:', erro);
      throw erro;
    }
  }

  async getDiamondsAccumulated(startDate?: string, endDate?: string): Promise<any> {
    try {
      const response = await httpInterceptor.get(`/diamonds/diamonds-accumulated${this.buildDateQuery(startDate, endDate)}`, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao buscar diamantes acumulados:', erro);
      throw erro;
    }
  }

  async getDiamondsRedeemed(startDate?: string, endDate?: string): Promise<any> {
    try {
      const response = await httpInterceptor.get(`/diamonds/diamonds-redeemed${this.buildDateQuery(startDate, endDate)}`, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao buscar diamantes resgatados:', erro);
      throw erro;
    }
  }

  async getDiamondsExpiring(days?: number): Promise<any> {
    try {
      const query = days ? `?days=${days}` : '';
      const response = await httpInterceptor.get(`/diamonds/diamonds-expiring${query}`, {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao buscar diamantes expirando:', erro);
      throw erro;
    }
  }

  // Ranking de usuários por diamantes
  async getRanking(): Promise<any[]> {
    try {
      const response = await httpInterceptor.get('/diamonds/ranking', {
        headers: this.getHeaders()
      });
      return await response.json();
    } catch (erro) {
      console.error('Erro ao buscar ranking de diamantes:', erro);
      throw erro;
    }
  }

  // Método helper para obter saldo do usuário (baseado no statements)
  async getSaldo(userId: number): Promise<number> {
    try {
      const statements = await this.getStatements(userId);
      let saldo = 0;
      
      statements.forEach(statement => {
        if (statement.tipo === 'credito') {
          saldo += statement.valor;
        } else if (statement.tipo === 'debito') {
          saldo -= statement.valor;
        }
      });
      
      return Math.max(0, saldo); // Garantir que não seja negativo
    } catch (erro) {
      console.error('Erro ao calcular saldo:', erro);
      return 0;
    }
  }
}

export const diamondsService = new DiamondsService();
